/**
 * /blog/:slug — individual Agency Blog post.
 *
 * Renders the post's HTML body, sets unique per-page <title> + meta
 * description, and injects a Schema.org/Article JSON-LD block so search
 * engines can surface the post as a rich-result.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2, AlertTriangle, ArrowRight, ChevronRight, Star, Mail, CheckCircle2, Compass } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import FooterBar from "@/components/FooterBar";
import ContactDialog from "@/components/ContactDialog";
import useSeo from "@/lib/useSeo";
import { getCategory } from "@/lib/blogCategories";
import { getRelatedSolutionsForPost } from "@/lib/blogSolutions";

const API = "/api";

function toAbsolute(url) {
    if (!url) return undefined;
    if (url.startsWith("http")) return url;
    if (typeof window === "undefined") return url;
    if (url.startsWith("/")) return `${window.location.origin}${url}`;
    return url;
}

/**
 * Frequently Asked Questions accordion rendered after the article body
 * on posts that have an authored ``faqs`` array. Uses native
 * <details>/<summary> for built-in accessibility, keyboard support,
 * and zero JavaScript overhead. The matching FAQPage JSON-LD is
 * injected from a useEffect in the parent component so Google can
 * surface these as rich snippets directly in SERP.
 */
function BlogFaqSection({ faqs }) {
    return (
        <section
            data-testid="blog-faq-section"
            aria-labelledby="blog-faq-heading"
            className="mt-12 rounded-2xl border border-[rgba(212,175,55,0.25)] bg-[#0b0e13] p-6 sm:p-8"
        >
            <p className="text-[10px] uppercase tracking-[0.3em] text-brand-goldSoft">
                Frequently asked questions
            </p>
            <h2
                id="blog-faq-heading"
                className="mt-2 font-serif text-2xl text-brand-ivory sm:text-3xl"
            >
                Quick answers, in plain English.
            </h2>

            <div className="mt-6 divide-y divide-[rgba(212,175,55,0.15)]">
                {faqs.map((f, i) => (
                    <details
                        key={i}
                        data-testid={`blog-faq-item-${i}`}
                        className="group py-4"
                    >
                        <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-base font-medium text-brand-ivory transition-colors hover:text-brand-gold2 focus:outline-none focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-brand-bg sm:text-lg">
                            <span className="flex-1 leading-snug">
                                {f.question}
                            </span>
                            <span
                                aria-hidden="true"
                                className="mt-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-brand-gold/40 text-brand-gold2 transition-transform group-open:rotate-45"
                            >
                                +
                            </span>
                        </summary>
                        <div
                            data-testid={`blog-faq-answer-${i}`}
                            className="mt-3 text-sm leading-relaxed text-brand-ivory/80 sm:text-base"
                        >
                            {f.answer}
                        </div>
                    </details>
                ))}
            </div>
        </section>
    );
}

/**
 * Internal-linking rail surfaced under every blog post. Picks 3 relevant
 * /insurance-solutions hub pages via lib/blogSolutions.js so the cluster
 * stays accurate even as new hub pages get added. Each link uses a
 * descriptive anchor (not the bare H1) to maximise topical-authority
 * signal to search engines.
 */
function RelatedSolutionsRail({ post }) {
    const picks = getRelatedSolutionsForPost(post);
    if (!picks.length) return null;
    return (
        <section
            data-testid="blog-related-solutions"
            aria-labelledby="related-solutions-heading"
            className="mt-10 rounded-2xl border border-[rgba(212,175,55,0.18)] bg-[#0b0e13]/70 p-6 sm:p-8"
        >
            <div className="flex items-start gap-3">
                <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[rgba(212,175,55,0.35)] bg-[#05070a]">
                    <Compass className="h-4 w-4 text-brand-gold" />
                </div>
                <div className="flex-1">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-brand-goldSoft">
                        Related insurance solutions
                    </p>
                    <h2
                        id="related-solutions-heading"
                        className="mt-2 font-serif text-2xl text-brand-ivory sm:text-3xl"
                    >
                        Where to go next.
                    </h2>
                    <p className="mt-2 text-sm text-brand-muted sm:text-base">
                        Three hub pages from across our practice that
                        connect to what you just read. Each one explains
                        the coverage in plain English and links to a
                        licensed agent.
                    </p>
                </div>
            </div>

            <ul className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
                {picks.map((p) => (
                    <li key={p.slug}>
                        <Link
                            to={p.path}
                            data-testid={`blog-related-solution-${p.slug}`}
                            className="group flex h-full flex-col justify-between rounded-xl border border-[rgba(212,175,55,0.18)] bg-[#05070a] p-4 transition-colors hover:border-[rgba(212,175,55,0.5)] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-brand-bg"
                        >
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.25em] text-brand-goldSoft">
                                    Hub page
                                </p>
                                <h3 className="mt-1 font-serif text-base leading-snug text-brand-ivory group-hover:text-brand-gold2 sm:text-lg">
                                    {p.label}
                                </h3>
                                <p className="mt-2 text-xs leading-relaxed text-brand-muted sm:text-sm">
                                    {p.blurb}
                                </p>
                            </div>
                            <span className="mt-4 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.25em] text-brand-gold2 group-hover:text-brand-gold">
                                Explore
                                <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                            </span>
                        </Link>
                    </li>
                ))}
            </ul>
        </section>
    );
}

function BlogSubscribeForm({ sourceSlug }) {
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState("idle"); // idle | loading | success | error
    const [message, setMessage] = useState("");

    async function submit(e) {
        e.preventDefault();
        const trimmed = email.trim();
        // Lightweight client-side guard — backend re-validates.
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
            setStatus("error");
            setMessage("Please enter a valid email address.");
            return;
        }
        setStatus("loading");
        setMessage("");
        try {
            await axios.post(`${API}/blog/subscribe`, {
                email: trimmed,
                source_slug: sourceSlug || null,
            });
            setStatus("success");
            setMessage("Thanks — you're on the list.");
            setEmail("");
        } catch {
            setStatus("error");
            setMessage("Something went wrong. Please try again in a moment.");
        }
    }

    return (
        <section
            data-testid="blog-subscribe-section"
            className="mt-12 rounded-2xl border border-[rgba(212,175,55,0.25)] bg-gradient-to-br from-[#0b0e13] via-[#101620] to-[#0b0e13] p-6 sm:p-8"
        >
            <div className="flex items-start gap-3">
                <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[rgba(212,175,55,0.35)] bg-[#05070a]">
                    <Mail className="h-4 w-4 text-brand-gold" />
                </div>
                <div className="flex-1">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-brand-goldSoft">
                        Get the Endurance briefing
                    </p>
                    <h2 className="mt-2 font-serif text-2xl text-brand-ivory sm:text-3xl">
                        Coverage notes, in your inbox.
                    </h2>
                    <p className="mt-2 text-sm text-brand-muted sm:text-base">
                        One short email when we publish a new article worth
                        your time. No spam. Unsubscribe in one click.
                    </p>

                    {status === "success" ? (
                        <div
                            data-testid="blog-subscribe-success"
                            className="mt-5 inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 text-xs uppercase tracking-[0.2em] text-emerald-200"
                        >
                            <CheckCircle2 className="h-4 w-4" />
                            {message}
                        </div>
                    ) : (
                        <form
                            onSubmit={submit}
                            noValidate
                            className="mt-5 flex flex-col gap-3 sm:flex-row"
                            data-testid="blog-subscribe-form"
                        >
                            <input
                                type="email"
                                required
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                data-testid="blog-subscribe-email"
                                className="flex-1 rounded-full border border-[rgba(212,175,55,0.3)] bg-[#05070a] px-5 py-3 text-sm text-brand-ivory placeholder:text-brand-muted/60 focus:border-brand-gold focus:outline-none focus:ring-2 focus:ring-brand-gold/40"
                            />
                            <button
                                type="submit"
                                disabled={status === "loading"}
                                data-testid="blog-subscribe-submit"
                                className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-gold bg-brand-gold px-6 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-brand-bg transition-colors hover:bg-brand-gold2 disabled:opacity-60"
                            >
                                {status === "loading" ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : null}
                                Subscribe
                            </button>
                        </form>
                    )}

                    {status === "error" ? (
                        <p
                            data-testid="blog-subscribe-error"
                            className="mt-3 text-xs text-rose-300"
                        >
                            {message}
                        </p>
                    ) : null}
                </div>
            </div>
        </section>
    );
}

export default function BlogPostPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [post, setPost] = useState(null);
    const [related, setRelated] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!slug) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        setRelated([]);
        // Fetch the post AND its related rail in parallel so the page
        // composes in a single render pass with no layout jump.
        Promise.all([
            axios.get(`${API}/blog/posts/${slug}`),
            axios
                .get(`${API}/blog/posts/${slug}/related?limit=2`)
                .catch(() => ({ data: [] })),
        ])
            .then(([postRes, relRes]) => {
                if (cancelled) return;
                setPost(postRes.data);
                setRelated(Array.isArray(relRes.data) ? relRes.data : []);
            })
            .catch((e) => {
                if (cancelled) return;
                const code = e?.response?.status;
                setError(
                    code === 404
                        ? "This post does not exist or has been removed."
                        : "We couldn't load this post right now. Please try again shortly."
                );
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [slug]);

    // Unique <title> + meta description (per-post overrides win). The
    // ``useSeo`` hook automatically suffixes the brand and sets the
    // canonical link + og:url for /blog/<slug>.
    useSeo({
        title: post && (post.seo_title || post.title),
        description: post && (post.seo_description || post.summary),
        canonicalPath: post ? `/blog/${post.slug}` : undefined,
        ogType: "article",
        ogImage: post ? toAbsolute(post.hero_image_url) : undefined,
    });

    const heroAbs = useMemo(() => toAbsolute(post?.hero_image_url), [post]);
    const category = useMemo(() => (post ? getCategory(post.category) : null), [post]);

    // Schema.org/Article JSON-LD with the user-requested fields.
    useEffect(() => {
        const NODE_ID = "ldjson-article";
        if (!post) {
            const old = document.getElementById(NODE_ID);
            if (old) old.remove();
            return;
        }
        const pageUrl =
            typeof window !== "undefined" ? window.location.href : "";
        const schema = {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.summary,
            datePublished: post.published_at,
            dateModified: post.updated_at || post.published_at,
            ...(heroAbs ? { image: [heroAbs] } : {}),
            mainEntityOfPage: {
                "@type": "WebPage",
                "@id": pageUrl,
            },
            ...(category ? { articleSection: category.label } : {}),
            author: {
                "@type": "Organization",
                name: post.author || "Endurance Casualty",
                url: "https://endurancecasualty.com/",
            },
            publisher: {
                "@type": "Organization",
                name: "Endurance Casualty Insurance Agency",
                url: "https://endurancecasualty.com/",
            },
        };
        let node = document.getElementById(NODE_ID);
        if (!node) {
            node = document.createElement("script");
            node.type = "application/ld+json";
            node.id = NODE_ID;
            document.head.appendChild(node);
        }
        node.textContent = JSON.stringify(schema);
        return () => {
            const cleanup = document.getElementById(NODE_ID);
            if (cleanup) cleanup.remove();
        };
    }, [post, heroAbs, category]);

    // BreadcrumbList JSON-LD — Google surfaces these as breadcrumb chips
    // in search results, which lifts CTR on category-tagged articles.
    useEffect(() => {
        const NODE_ID = "ldjson-breadcrumb";
        if (!post || !category) {
            const old = document.getElementById(NODE_ID);
            if (old) old.remove();
            return;
        }
        const origin =
            typeof window !== "undefined"
                ? window.location.origin
                : "https://endurancecasualty.com";
        const schema = {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                {
                    "@type": "ListItem",
                    position: 1,
                    name: "Agency Blog",
                    item: `${origin}/blog`,
                },
                {
                    "@type": "ListItem",
                    position: 2,
                    name: category.label,
                    item: `${origin}/blog/category/${category.slug}`,
                },
                {
                    "@type": "ListItem",
                    position: 3,
                    name: post.title,
                    item: `${origin}/blog/${post.slug}`,
                },
            ],
        };
        let node = document.getElementById(NODE_ID);
        if (!node) {
            node = document.createElement("script");
            node.type = "application/ld+json";
            node.id = NODE_ID;
            document.head.appendChild(node);
        }
        node.textContent = JSON.stringify(schema);
        return () => {
            const cleanup = document.getElementById(NODE_ID);
            if (cleanup) cleanup.remove();
        };
    }, [post, category]);

    // FAQPage JSON-LD — only emitted when the post has an authored
    // ``faqs`` array. Google surfaces these as expandable rich-snippet
    // accordions directly in SERPs, which materially lifts click-through
    // on long-tail queries.
    useEffect(() => {
        const NODE_ID = "ldjson-faqpage";
        const faqs = (post && post.faqs) || [];
        if (!faqs.length) {
            const old = document.getElementById(NODE_ID);
            if (old) old.remove();
            return;
        }
        const schema = {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
                "@type": "Question",
                name: f.question,
                acceptedAnswer: {
                    "@type": "Answer",
                    // schema.org's "Answer.text" should be plain-text
                    // (HTML is permitted but Google strips tags for
                    // ranking) — our authored answers are plain prose.
                    text: f.answer,
                },
            })),
        };
        let node = document.getElementById(NODE_ID);
        if (!node) {
            node = document.createElement("script");
            node.type = "application/ld+json";
            node.id = NODE_ID;
            document.head.appendChild(node);
        }
        node.textContent = JSON.stringify(schema);
        return () => {
            const cleanup = document.getElementById(NODE_ID);
            if (cleanup) cleanup.remove();
        };
    }, [post]);

    return (
        <main
            data-testid="blog-post-page"
            className="flex min-h-screen flex-col bg-brand-bg text-brand-ivory"
        >
            <PageHeader />
            <section className="flex-1 px-5 py-12 sm:px-8">
                <div className="mx-auto max-w-3xl">
                    <Link
                        to="/blog"
                        data-testid="blog-back-link"
                        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-brand-gold2 hover:text-brand-gold"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" />
                        Back to Agency Blog
                    </Link>

                    {loading ? (
                        <div className="mt-10 flex items-center gap-3 rounded-2xl border border-[rgba(212,175,55,0.18)] bg-[#0b0e13] p-8 text-sm text-brand-muted">
                            <Loader2 className="h-4 w-4 animate-spin text-brand-gold" />
                            Loading…
                        </div>
                    ) : error ? (
                        <div
                            data-testid="blog-post-error"
                            className="mt-10 rounded-2xl border border-rose-500/30 bg-rose-500/10 p-8 text-rose-200"
                        >
                            <div className="flex items-start gap-3">
                                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
                                <div>
                                    <p className="font-semibold">
                                        We couldn&apos;t open this post
                                    </p>
                                    <p className="mt-1 text-sm opacity-90">
                                        {error}
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => navigate("/blog")}
                                        className="mt-4 inline-flex items-center gap-2 rounded-full border border-rose-300/50 px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-rose-500/20"
                                    >
                                        Browse the blog
                                        <ArrowRight className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : post ? (
                        <article className="mt-8">
                            {category && (
                                <nav
                                    aria-label="Breadcrumb"
                                    data-testid="blog-breadcrumb"
                                    className="mb-6 flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-brand-goldSoft"
                                >
                                    <Link
                                        to="/blog"
                                        className="hover:text-brand-gold"
                                    >
                                        Blog
                                    </Link>
                                    <ChevronRight className="h-3 w-3 text-brand-muted" />
                                    <Link
                                        to={`/blog/category/${category.slug}`}
                                        className="hover:text-brand-gold"
                                    >
                                        {category.shortLabel || category.label}
                                    </Link>
                                    <ChevronRight className="h-3 w-3 text-brand-muted" />
                                    <span
                                        className="line-clamp-1 max-w-[40ch] truncate normal-case tracking-normal text-brand-muted"
                                        title={post.title}
                                    >
                                        {post.title}
                                    </span>
                                </nav>
                            )}
                            <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-brand-goldSoft">
                                {post.is_sticky ? (
                                    <span
                                        data-testid="blog-post-featured-badge"
                                        className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(212,175,55,0.45)] bg-[rgba(212,175,55,0.12)] px-2.5 py-0.5 text-[10px] uppercase tracking-[0.3em] text-brand-gold"
                                    >
                                        <Star className="h-3 w-3 fill-current" />
                                        Featured
                                    </span>
                                ) : null}
                                {post.author ? (
                                    <span className="normal-case tracking-normal text-brand-muted">
                                        By {post.author}
                                    </span>
                                ) : null}
                                {category && (
                                    <Link
                                        to={`/blog/category/${category.slug}`}
                                        data-testid={`blog-category-badge-${category.slug}`}
                                        className="inline-flex items-center rounded-full border border-[rgba(212,175,55,0.35)] bg-[rgba(212,175,55,0.08)] px-2.5 py-0.5 text-[9px] uppercase tracking-[0.25em] text-brand-gold2 transition-colors hover:bg-[rgba(212,175,55,0.18)]"
                                    >
                                        {category.shortLabel || category.label}
                                    </Link>
                                )}
                            </div>
                            <h1
                                data-testid="blog-post-title"
                                className="mt-3 font-serif text-3xl text-brand-ivory sm:text-4xl lg:text-5xl"
                            >
                                {post.title}
                            </h1>
                            <p
                                data-testid="blog-post-summary"
                                className="mt-4 text-base text-brand-ivory/80 sm:text-lg"
                            >
                                {post.summary}
                            </p>
                            {post.hero_image_url ? (
                                <div className="mt-8 overflow-hidden rounded-2xl border border-[rgba(212,175,55,0.25)] bg-black shadow-[0_30px_60px_rgba(0,0,0,0.5)]">
                                    <img
                                        src={post.hero_image_url}
                                        alt={`${post.title} — hero illustration`}
                                        className="aspect-[16/9] w-full object-cover"
                                    />
                                </div>
                            ) : null}
                            <div
                                data-testid="blog-post-body"
                                className="prose prose-invert prose-headings:font-serif prose-headings:text-brand-ivory prose-a:text-brand-gold2 hover:prose-a:text-brand-gold mt-10 max-w-none text-base leading-relaxed text-brand-ivory/90"
                                // Content is admin-authored HTML. The admin
                                // form sanitises on the way in.
                                dangerouslySetInnerHTML={{
                                    __html: post.content_html,
                                }}
                            />

                            {post.faqs && post.faqs.length > 0 ? (
                                <BlogFaqSection faqs={post.faqs} />
                            ) : null}

                            <RelatedSolutionsRail post={post} />

                            <BlogSubscribeForm sourceSlug={post.slug} />

                            <div className="mt-12 rounded-2xl border border-[rgba(212,175,55,0.25)] bg-[#0b0e13] p-6 sm:p-8">
                                <p className="text-[10px] uppercase tracking-[0.3em] text-brand-goldSoft">
                                    Questions on this topic?
                                </p>
                                <h2 className="mt-2 font-serif text-2xl text-brand-ivory sm:text-3xl">
                                    Talk to a licensed Endurance agent.
                                </h2>
                                <p className="mt-3 text-sm text-brand-muted sm:text-base">
                                    No obligation. Real guidance from licensed
                                    humans — by phone, text, or email.
                                </p>
                                <div className="mt-6">
                                    <ContactDialog
                                        defaultMessage={`I'd like to discuss "${post.title}".`}
                                        trigger={
                                            <button
                                                type="button"
                                                data-testid="blog-post-cta"
                                                className="inline-flex items-center gap-2 rounded-full border border-brand-gold bg-brand-gold px-6 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-brand-bg transition-colors hover:bg-brand-gold2"
                                            >
                                                Start the conversation
                                                <ArrowRight className="h-4 w-4" />
                                            </button>
                                        }
                                    />
                                </div>
                            </div>

                            {related.length > 0 ? (
                                <section
                                    data-testid="blog-related-posts"
                                    className="mt-16"
                                >
                                    <div className="flex items-end justify-between gap-4">
                                        <div>
                                            <p className="text-[10px] uppercase tracking-[0.3em] text-brand-goldSoft">
                                                Continue reading
                                            </p>
                                            <h2 className="mt-1 font-serif text-2xl text-brand-ivory sm:text-3xl">
                                                Related posts
                                            </h2>
                                        </div>
                                        <Link
                                            to="/blog"
                                            className="hidden text-[10px] uppercase tracking-[0.3em] text-brand-gold2 hover:text-brand-gold sm:inline-flex sm:items-center sm:gap-1.5"
                                        >
                                            All posts
                                            <ArrowRight className="h-3 w-3" />
                                        </Link>
                                    </div>
                                    <div className="gold-hairline my-4 w-24" />
                                    <ul className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                                        {related.map((r) => (
                                            <li key={r.slug}>
                                                <Link
                                                    to={`/blog/${r.slug}`}
                                                    data-testid={`blog-related-${r.slug}`}
                                                    className="group block h-full overflow-hidden rounded-2xl border border-[rgba(212,175,55,0.18)] bg-[#0b0e13] transition-colors hover:border-[rgba(212,175,55,0.45)] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                                                >
                                                    {r.hero_image_url ? (
                                                        <div className="aspect-[16/9] overflow-hidden bg-black">
                                                            <img
                                                                src={r.hero_image_url}
                                                                alt={`${r.title} hero`}
                                                                loading="lazy"
                                                                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div className="aspect-[16/9] bg-gradient-to-br from-[#0b0e13] via-[#101620] to-[#0b0e13]" />
                                                    )}
                                                    <div className="p-5">
                                                        {r.is_sticky ? (
                                                            <span className="inline-flex items-center gap-1 rounded-full border border-[rgba(212,175,55,0.35)] bg-[rgba(212,175,55,0.08)] px-2 py-0.5 text-[9px] uppercase tracking-[0.25em] text-brand-gold">
                                                                <Star className="h-2.5 w-2.5 fill-current" />
                                                                Featured
                                                            </span>
                                                        ) : null}
                                                        <h3 className="mt-2 font-serif text-lg leading-snug text-brand-ivory sm:text-xl">
                                                            {r.title}
                                                        </h3>
                                                        <p className="mt-2 line-clamp-2 text-sm text-brand-muted">
                                                            {r.summary}
                                                        </p>
                                                        <p className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-brand-gold2">
                                                            Read post
                                                            <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1" />
                                                        </p>
                                                    </div>
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            ) : null}
                        </article>
                    ) : null}
                </div>
            </section>
            <FooterBar />
        </main>
    );
}
